import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Loader2, Mic, Square, Users, X } from 'lucide-react';
import { useMeetingRecorder } from './MeetingRecorderContext';

export const formatClock = (seconds) => {
  const total = Math.max(0, Math.floor(seconds || 0));
  const h = Math.floor(total / 3600);
  const m = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
  const s = String(total % 60).padStart(2, '0');
  return h ? `${h}:${m}:${s}` : `${m}:${s}`;
};

const LevelMeter = ({ level }) => (
  <div className="flex h-5 items-end gap-0.5" aria-hidden="true">
    {[0.15, 0.35, 0.55, 0.75, 0.9].map((threshold) => (
      <span key={threshold}
        className={`w-1 rounded-sm transition-colors ${level >= threshold ? 'bg-rose-500' : 'bg-slate-200'}`}
        style={{ height: `${30 + threshold * 70}%` }} />
    ))}
  </div>
);

// Session expirée pendant l'enregistrement : cet onglet garde l'audio, la reconnexion se fait dans un autre.
const SessionExpired = () => (
  <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-amber-50 p-2 text-xs text-amber-800">
    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
    <span>
      Ta session Deepflow a expiré. L’enregistrement continue et l’audio reste gardé dans cet onglet :{' '}
      <a href="/login" target="_blank" rel="noopener noreferrer" className="font-semibold underline">reconnecte-toi dans un nouvel onglet</a>
      , sans fermer celui-ci ; l’envoi reprendra tout seul.
    </span>
  </p>
);

// Barre flottante en bas à gauche (Nexus occupe la droite) : visible sur toutes les pages.
const RecorderBar = () => {
  const recorder = useMeetingRecorder();
  const { phase, meeting, projectName, elapsed, level, upload, warning, error, sessionExpired } = recorder;
  const [confirmCancel, setConfirmCancel] = useState(false);

  if (phase === 'idle') return null;

  const shell = 'fixed bottom-4 left-4 z-50 w-[calc(100vw-2rem)] max-w-sm rounded-2xl border bg-white p-3 shadow-xl';

  if (phase === 'starting') {
    return (
      <div className={`${shell} border-slate-200`}>
        <p className="flex items-center gap-2 text-sm text-slate-600"><Loader2 className="h-4 w-4 animate-spin" />Ouverture du micro…</p>
      </div>
    );
  }

  if (phase === 'recording') {
    return (
      <div className={`${shell} border-rose-200`}>
        <div className="flex items-center gap-3">
          <span className="relative flex h-3 w-3 shrink-0">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-60" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-rose-600" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-slate-900">{meeting?.title}</p>
            <p className="truncate text-xs text-slate-500">{projectName || 'Sans projet'}</p>
          </div>
          <LevelMeter level={level} />
          <span className="w-14 text-right font-mono text-sm tabular-nums text-slate-700">{formatClock(elapsed)}</span>
        </div>
        {sessionExpired && <SessionExpired />}
        {!sessionExpired && (warning || upload.retrying) && (
          <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {warning || 'Connexion instable : l’audio est gardé et sera renvoyé.'}
          </p>
        )}
        <div className="mt-3 flex gap-2">
          {confirmCancel ? (
            <>
              <button type="button" onClick={recorder.cancel}
                className="flex-1 rounded-xl bg-slate-800 px-3 py-2 text-xs font-semibold text-white">Oui, tout jeter</button>
              <button type="button" onClick={() => setConfirmCancel(false)}
                className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600">Continuer</button>
            </>
          ) : (
            <>
              <button type="button" onClick={recorder.stop}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700">
                <Square className="h-3.5 w-3.5 fill-current" /> Terminer
              </button>
              <button type="button" onClick={() => setConfirmCancel(true)} title="Abandonner sans rien garder"
                className="rounded-xl border border-slate-200 px-3 py-2 text-slate-500 hover:bg-slate-50" aria-label="Abandonner">
                <X className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (phase === 'sending') {
    return (
      <div className={`${shell} border-slate-200`}>
        <p className="flex items-center gap-2 text-sm text-slate-700">
          <Loader2 className="h-4 w-4 animate-spin" />
          Envoi de la fin de l'enregistrement{upload.pending ? ` (${upload.pending} restant${upload.pending > 1 ? 's' : ''})` : ''}…
        </p>
        <p className="mt-1 text-xs text-slate-400">Garde cet onglet ouvert quelques secondes.</p>
        {sessionExpired && <SessionExpired />}
      </div>
    );
  }

  if (phase === 'conflict') {
    const { conflict } = recorder;
    return (
      <div className={`${shell} border-amber-200`}>
        <div className="flex items-start gap-2">
          <Users className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900">{conflict?.by || 'Un collègue'} enregistre déjà cette réunion</p>
            <p className="text-xs text-slate-500">Inutile d'enregistrer deux fois : suis-la, tu recevras la synthèse comme participant.</p>
          </div>
          <button type="button" onClick={recorder.dismiss} className="text-slate-400 hover:text-slate-600" aria-label="Fermer">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-3 flex gap-2">
          <button type="button" onClick={recorder.joinTwin}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-700">
            <Users className="h-3.5 w-3.5" /> Suivre sans enregistrer
          </button>
          <button type="button" onClick={recorder.recordAnyway} title="Par exemple si tu es dans une autre salle"
            className="flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50">
            <Mic className="h-3.5 w-3.5" /> Enregistrer quand même
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'joined') {
    return (
      <div className={`${shell} border-emerald-200`}>
        <div className="flex items-start gap-2">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900">Tu suis « {meeting?.title} »</p>
            <p className="text-xs text-slate-500">{recorder.conflict?.by || 'Ton collègue'} enregistre ; tu seras prévenu quand la synthèse sera prête.</p>
            {meeting && (
              <Link to={`/meetings/${meeting.id}`} onClick={recorder.dismiss} className="mt-1 inline-block text-xs font-semibold text-blue-600 hover:underline">
                Ouvrir la réunion
              </Link>
            )}
          </div>
          <button type="button" onClick={recorder.dismiss} className="text-slate-400 hover:text-slate-600" aria-label="Fermer">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'done') {
    return (
      <div className={`${shell} border-emerald-200`}>
        <div className="flex items-start gap-2">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900">Réunion envoyée</p>
            <p className="text-xs text-slate-500">La transcription démarre sur le Mac ; tu seras prévenu quand la synthèse sera prête.</p>
            {meeting && (
              <Link to={`/meetings/${meeting.id}`} onClick={recorder.dismiss} className="mt-1 inline-block text-xs font-semibold text-blue-600 hover:underline">
                Suivre la réunion
              </Link>
            )}
          </div>
          <button type="button" onClick={recorder.dismiss} className="text-slate-400 hover:text-slate-600" aria-label="Fermer">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${shell} border-rose-200`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-slate-800">{error}</p>
          {sessionExpired && <SessionExpired />}
          <div className="mt-2 flex gap-2">
            {recorder.canRetry && (
              <button type="button" onClick={recorder.retrySend}
                className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white">Réessayer l'envoi</button>
            )}
            {!recorder.canRetry && (
              <button type="button" onClick={recorder.dismiss}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600">Fermer</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecorderBar;
